import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getDefaultStoreCode } from '../utils/appConfig';

const extractAccountSession = (payload = {}, fallbackAccountCode = '') => {
  const currentAccount = payload.current_account || null;
  const accountCode = payload.account_code || currentAccount?.account_code || fallbackAccountCode || '';
  const accountId = payload.account_id ?? currentAccount?.id ?? null;

  return {
    currentAccount,
    accountCode,
    accountId,
  };
};

// Auth Store
export const useAuthStore = create(
  persist(
    (set) => ({
      user: null,
      token: null,
      accountId: null,
      accountCode: '',
      currentAccount: null,
      isAuthenticated: false,
      login: (user, token, accountCode = '', accountPayload = {}) => set({
        user,
        token,
        ...extractAccountSession(accountPayload, accountCode),
        isAuthenticated: true,
      }),
      logout: () => {
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem('token');
          window.localStorage.removeItem('access_token');
          window.localStorage.removeItem('access');
          window.localStorage.removeItem('account_code');
        }

        set({
          user: null,
          token: null,
          accountId: null,
          accountCode: '',
          currentAccount: null,
          isAuthenticated: false,
        });
      },
      updateUser: (user) => set((state) => ({
        user,
        ...extractAccountSession(user, state.accountCode),
      })),
    }),
    {
      name: 'auth-storage',
    }
  )
);

// Cart Store
export const useCartStore = create((set, get) => ({
  items: [],
  selectedStore: getDefaultStoreCode(''),
  selectedClient: null,
  
  setSelectedStore: (store) => set({ selectedStore: store }),
  setSelectedClient: (client) => set({ selectedClient: client }),
  
  addItem: (product, quantity = 1) => {
    const { items } = get();
    const existingItem = items.find((item) => item.product.id === product.id);
    
    if (existingItem) {
      set({
        items: items.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        ),
      });
    } else {
      set({ items: [...items, { product, quantity }] });
    }
  },
  
  removeItem: (productId) =>
    set((state) => ({
      items: state.items.filter((item) => item.product.id !== productId),
    })),
  
  updateQuantity: (productId, quantity) =>
    set((state) => ({
      items: state.items.map((item) =>
        item.product.id === productId ? { ...item, quantity } : item
      ),
    })),

  updateItemProduct: (productId, updates) =>
    set((state) => ({
      items: state.items.map((item) =>
        item.product.id === productId
          ? { ...item, product: { ...item.product, ...updates } }
          : item
      ),
    })),
  
  clearCart: () => set({ items: [], selectedClient: null }),
  
  getTotal: () => {
    const { items } = get();
    return items.reduce(
      (total, item) => total + item.product.price_ttc * item.quantity,
      0
    );
  },
  
  getTotalHT: () => {
    const { items } = get();
    return items.reduce(
      (total, item) => total + item.product.price_ht * item.quantity,
      0
    );
  },
}));

// Products Store
export const useProductsStore = create((set, get) => ({
  products: [],
  viewMode: 'grid',
  searchQuery: '',
  selectedCategory: null,
  
  setProducts: (products) => set({ products }),
  setViewMode: (mode) => set({ viewMode: mode }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  setSelectedCategory: (category) => set({ selectedCategory: category }),
  
  getFilteredProducts: () => {
    const { products, searchQuery, selectedCategory } = get();
    return products.filter((product) => {
      const matchesSearch =
        (product.name && product.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (product.reference && product.reference.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory =
        !selectedCategory || product.category?.id === selectedCategory;
      return matchesSearch && matchesCategory && product.is_visible;
    });
  },
}));
